/**
 * AuthGate — top-level wrapper around arbi-docs's shell. Forked from arbi-dev's
 * AdminGate (ADR 37) but WITHOUT the admin-only gate: arbi-docs is readable by
 * ANY authenticated role (Architecture Rule 18 — the help subdomain is
 * auth-gated, not public; published docs/releases are all-role-readable incl.
 * dist_rep, the external partner).
 *
 * State machine:
 *   loading        → LoadingScreen
 *   !user          → cross-subdomain redirect to arbi.ironcladsupply.co/login
 *   user && !role  → LoadingScreen (profile fetch in flight)
 *   any role       → render children
 *
 * `window.location.replace` is correct over React Router's <Navigate> because
 * this is a cross-origin hard navigation. In dev mode (vite dev server, vercel
 * preview URLs) the redirect is logged-and-skipped so local development doesn't
 * bounce to prod arbi.
 */
import { type JSX, type ReactNode } from 'react';
import { useApp } from '../hooks/useApp';
import LoadingScreen from './LoadingScreen';

const ARBI_LOGIN_URL = 'https://arbi.ironcladsupply.co/login?next=arbi-docs';

function redirectIfPossible(target: string): void {
  if (typeof window === 'undefined') return;
  if (import.meta.env.PROD) {
    window.location.replace(target);
  } else {
    console.warn('[arbi-docs/AuthGate] Would redirect to ' + target + ' (dev mode — skipping)');
  }
}

interface AuthGateProps {
  children: ReactNode;
}

export default function AuthGate({ children }: AuthGateProps): JSX.Element {
  const { user, role, loading } = useApp();

  if (loading) return <LoadingScreen />;

  if (!user) {
    redirectIfPossible(ARBI_LOGIN_URL);
    return <LoadingScreen message="Redirecting to sign in…" />;
  }

  if (!role) return <LoadingScreen />;

  return <>{children}</>;
}
